// src/constants/orderLabels.ts

import { OrderStatus } from '@models/order';
import { ProductSaleType } from '@apis/product';
import type { ChipProps } from '@mui/material';

/* ============================================================================
 *  ProductSaleType → User-friendly display labels
 * ========================================================================== */

export const PRODUCT_SALE_TYPE_LABELS: Record<ProductSaleType, string> = {
  [ProductSaleType.REAL_TIME]: 'Real-time',
  [ProductSaleType.RESERVATION]: 'Reservation',
};

/**
 * Safely return a display label for ProductSaleType.
 */
export const getProductSaleTypeLabel = (
  saleType?: ProductSaleType | string | null
): string => {
  if (!saleType) return '';
  const key = saleType as ProductSaleType;
  return PRODUCT_SALE_TYPE_LABELS[key] ?? String(saleType);
};

/* ============================================================================
 *  OrderStatus → User-friendly display labels
 * ========================================================================== */

export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  [OrderStatus.OK]: 'Completed',
  [OrderStatus.FAILED]: 'Failed',
  [OrderStatus.FULL_REFUNDED]: 'Refunded',
  [OrderStatus.PARTIAL_REFUNDED]: 'Refunded (Partially)',
  [OrderStatus.PENDING]: 'Pending',
};

/**
 * Safely return a display label for OrderStatus.
 */
export const getOrderStatusLabel = (
  status?: OrderStatus | string | null
): string => {
  if (!status) return '';
  const key = status as OrderStatus;
  return ORDER_STATUS_LABELS[key] ?? String(status);
};

/* ============================================================================
 *  OrderStatus → MUI Chip color mapping
 *  (Used for tables, detail pages, status badges)
 * ========================================================================== */

export const getOrderStatusChipColor = (
  status: OrderStatus | string
): ChipProps['color'] => {
  switch (status) {
    case OrderStatus.OK:
      return 'success';
    case OrderStatus.FAILED:
      return 'error';
    case OrderStatus.PENDING:
      return 'warning';
    case OrderStatus.PARTIAL_REFUNDED:
    case OrderStatus.FULL_REFUNDED:
      return 'default';
    default:
      return 'default';
  }
};
